type TQueryValue = string | number | boolean;

interface ILegacyTableParams {
  pagination?: { page?: number; rowsPerPage?: number; sort?: string };
  filters?: Record<string, Record<string, unknown> | undefined>;
  sort?: string;
}

const isEmpty = (value: unknown) => value === undefined || value === null || value === '';

// The table date filters hold epoch milliseconds; the backend date pipes only accept date strings.
const toDateParam = (value: unknown) =>
  typeof value === 'number' ? new Date(value).toISOString() : (value as TQueryValue);

/**
 * Translates the legacy Redux table state ({ pagination, filters: { field: { $op: value } }, sort: 'field,DESC' })
 * into the plain query params of the qsupply/backend `api/v1/cms/...` list endpoints.
 * Date ranges ({ gte, lte }) become `<field>From` / `<field>To`; any other filter sends its single value as `<field>`.
 * Callers without pagination (dropdown option lists) get the first `defaultLimit` rows.
 */
export const toCmsListQuery = ({ pagination, filters, sort }: ILegacyTableParams = {}, defaultLimit = 100) => {
  const query: Record<string, TQueryValue> = {
    page: pagination?.page || 1,
    limit: pagination?.rowsPerPage || defaultLimit,
  };

  const [sortField, sortOrder] = (sort || pagination?.sort || '').split(',');
  if (sortField && sortOrder) query.sort = `${sortField}.${sortOrder.toLowerCase()}`;

  Object.entries(filters || {}).forEach(([field, condition = {}]) => {
    if ('gte' in condition || 'lte' in condition) {
      if (!isEmpty(condition.gte)) query[`${field}From`] = toDateParam(condition.gte);
      if (!isEmpty(condition.lte)) query[`${field}To`] = toDateParam(condition.lte);
      return;
    }
    const value = Object.values(condition)[0];
    if (!isEmpty(value)) query[field] = value as TQueryValue;
  });

  return query;
};
